import { useEffect, useState } from 'react';

interface LeavesEffectProps {
  intensity?: 'light' | 'medium' | 'heavy';
}

interface Leaf {
  id: number;
  left: number;
  size: number;
  animationDuration: number;
  animationDelay: number;
  color: string;
  rotation: number;
}

const LeavesEffect = ({ intensity = 'medium' }: LeavesEffectProps) => {
  const [leaves, setLeaves] = useState<Leaf[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  const leafCounts = {
    light: 20,
    medium: 40,
    heavy: 70,
  };

  const leafColors = [
    '#D2691E',
    '#FF6347',
    '#DAA520',
    '#8B4513',
    '#CD853F',
    '#B8860B',
    '#A0522D',
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const baseCount = leafCounts[intensity];
    const count = isMobile ? Math.floor(baseCount * 0.5) : baseCount;
    const newLeaves: Leaf[] = [];

    for (let i = 0; i < count; i++) {
      newLeaves.push({
        id: i,
        left: Math.random() * 100,
        size: Math.random() * 12 + 8,
        animationDuration: Math.random() * 8 + 6,
        animationDelay: Math.random() * 10,
        color: leafColors[Math.floor(Math.random() * leafColors.length)],
        rotation: Math.random() * 360,
      });
    }

    setLeaves(newLeaves);
  }, [intensity, isMobile]);

  return (
    <div 
      className="fixed inset-0 pointer-events-none z-40 overflow-hidden"
      style={{ height: '100dvh', minHeight: '100vh' }}
    >
      <style>{`
        @keyframes fall-leaf {
          0% {
            transform: translateY(-10dvh) rotate(0deg) translateX(0) translateZ(0);
            opacity: 1;
          }
          25% {
            transform: translateY(22dvh) rotate(90deg) translateX(30px) translateZ(0);
          }
          50% {
            transform: translateY(45dvh) rotate(180deg) translateX(-20px) translateZ(0);
          }
          75% {
            transform: translateY(67dvh) rotate(270deg) translateX(25px) translateZ(0);
          }
          100% {
            transform: translateY(100dvh) rotate(360deg) translateX(-15px) translateZ(0);
            opacity: 0.3;
          }
        }
        
        .leaf {
          position: absolute;
          pointer-events: none;
          animation: fall-leaf linear infinite;
          will-change: transform, opacity;
          backface-visibility: hidden;
        }
        
        .leaf-shape {
          width: 100%;
          height: 100%;
          clip-path: polygon(50% 0%, 80% 30%, 100% 50%, 80% 70%, 50% 100%, 20% 70%, 0% 50%, 20% 30%);
        }
        
        @media (prefers-reduced-motion: reduce) {
          .leaf {
            animation: none !important;
            opacity: 0 !important;
          }
        }
      `}</style>
      
      {leaves.map((leaf) => (
        <div
          key={leaf.id}
          className="leaf"
          style={{
            left: `${leaf.left}%`,
            width: `${leaf.size}px`,
            height: `${leaf.size * 1.2}px`,
            animationDuration: `${leaf.animationDuration}s`,
            animationDelay: `${leaf.animationDelay}s`,
            transform: `rotate(${leaf.rotation}deg)`,
          }}
        >
          <div
            className="leaf-shape"
            style={{
              backgroundColor: leaf.color,
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default LeavesEffect;
